/**
 * CrewBrain — Widget flotante para preguntar en lenguaje natural.
 *
 * Atajo: ⌘+J (Mac) / Ctrl+J (Win)
 * Hace POST /api/brain/ask con la pregunta.
 */
import { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { Brain, X, Loader2, Send, Sparkles } from "../lib/icons.jsx";
import { api } from "../api/client.js";
import { cn } from "../lib/utils.js";

const SUGERENCIAS = [
  "¿Cuántas tareas se publicaron este mes?",
  "Clientes con tasa de aprobación bajo 70%",
  "Top 5 clientes por horas trabajadas en mayo",
  "Facturas vencidas hace más de 30 días",
  "Personas con más de 40 horas esta semana",
  "Tareas atascadas en revisión hace más de 5 días",
];

export default function CrewBrain() {
  const [open, setOpen] = useState(false);
  const [pregunta, setPregunta] = useState("");
  const [resultado, setResultado] = useState(null);
  const [loading, setLoading] = useState(false);
  const [historial, setHistorial] = useState([]);
  const inputRef = useRef(null);
  // El botón es flotante y se puede arrastrar a cualquier parte de la pantalla; la posición se recuerda en este dispositivo.
  const { pathname } = useLocation();
  const enChat = pathname.startsWith("/chat");
  const [pos, setPos] = useState(() => { try { const p = JSON.parse(localStorage.getItem("crewbrain-pos") || "null"); return p && typeof p.x === "number" ? p : null; } catch { return null; } });
  const [, redibujar] = useState(0);
  const arrastre = useRef(null);
  const TAM = 48;
  const limitar = (x, y) => ({ x: Math.max(8, Math.min(window.innerWidth - TAM - 8, x)), y: Math.max(8, Math.min(window.innerHeight - TAM - 8, y)) });
  useEffect(() => { const r = () => redibujar((n) => n + 1); window.addEventListener("resize", r); return () => window.removeEventListener("resize", r); }, []);
  function alApretar(e) {
    const r = e.currentTarget.getBoundingClientRect();
    arrastre.current = { dx: e.clientX - r.left, dy: e.clientY - r.top, x0: e.clientX, y0: e.clientY, movio: false };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  }
  function alMover(e) {
    const a = arrastre.current; if (!a) return;
    if (!a.movio && Math.hypot(e.clientX - a.x0, e.clientY - a.y0) < 6) return;
    a.movio = true;
    setPos(limitar(e.clientX - a.dx, e.clientY - a.dy));
  }
  function alSoltar(e) {
    const a = arrastre.current; arrastre.current = null;
    if (!a) return;
    if (a.movio) { const p = limitar(e.clientX - a.dx, e.clientY - a.dy); setPos(p); try { localStorage.setItem("crewbrain-pos", JSON.stringify(p)); } catch { /* sin storage */ } }
    else setOpen(true);
  }
  const lugar = pos ? limitar(pos.x, pos.y) : null;

  // Atajo de teclado: Ctrl/Cmd + J
  useEffect(() => {
    function onKey(e) {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "j") {
        e.preventDefault();
        setOpen(o => !o);
      }
      if (e.key === "Escape") setOpen(false);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    const abrir = () => setOpen(true);
    window.addEventListener("crew-abrir-brain", abrir);
    return () => window.removeEventListener("crew-abrir-brain", abrir);
  }, []);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
  }, [open]);

  async function preguntar(q) {
    if (!q || q.trim().length < 3) return;
    setLoading(true);
    setResultado(null);
    try {
      const r = await api.brainAsk(q);
      setResultado(r);
      setHistorial(h => [{ pregunta: q, respuesta: r.respuesta, total: r.total }, ...h].slice(0, 10));
      setPregunta("");
    } catch (err) {
      setResultado({ error: err.message });
    } finally { setLoading(false); }
  }

  return (
    <>
      {/* Floating button */}
      <button
        onPointerDown={alApretar} onPointerMove={alMover} onPointerUp={alSoltar} onPointerCancel={() => { arrastre.current = null; }}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpen(true); } }}
        title="Crew Brain · tocá para preguntar, mantené y arrastrá para moverlo (Ctrl+J)"
        aria-label="Crew Brain"
        style={lugar ? { left: lugar.x, top: lugar.y, touchAction: "none" } : { touchAction: "none" }}
        className={cn(
          "fixed z-40 w-12 h-12 rounded-full cursor-grab active:cursor-grabbing select-none",
          !lugar && (enChat ? "md:bottom-5 md:right-5" : "bottom-[100px] md:bottom-5 right-5"),
          enChat && "!hidden",   // en el chat el chat ya tiene su asistente: el botón flotante solo tapaba los mensajes
          " bg-[#7C3AED] ",
          "shadow-lg hover:shadow-xl transition-all hover:scale-105",
          "flex items-center justify-center text-white",
          open && "opacity-0 pointer-events-none"
        )}>
        <Brain className="h-5 w-5" />
      </button>

      {/* Modal */}
      {open && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-[#111318]/40 backdrop-blur-sm sm:pt-20"
             onClick={() => setOpen(false)}>
          <div onClick={e => e.stopPropagation()}
               className="w-full sm:max-w-2xl bg-white sm:rounded-2xl shadow-2xl overflow-hidden max-sm:h-full max-sm:flex max-sm:flex-col">
            {/* Header */}
            <div className="bg-[#7C3AED] text-white p-4 max-sm:pt-[max(14px,env(safe-area-inset-top))] flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-2">
                <Brain className="h-5 w-5" />
                <span className="font-bold">Crew Brain</span>
                <span className="text-xs opacity-80 max-sm:hidden">· Preguntale a tu data</span>
              </div>
              <button onClick={() => setOpen(false)} aria-label="Cerrar" className="h-11 w-11 -mr-2 flex items-center justify-center hover:bg-white/10 rounded-full">
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Input */}
            <div className="p-4 border-b border-border flex-shrink-0">
              <form onSubmit={e => { e.preventDefault(); preguntar(pregunta); }} className="flex gap-2 max-sm:flex-col">
                <input ref={inputRef}
                       value={pregunta}
                       onChange={e => setPregunta(e.target.value)}
                       placeholder="¿Cuántas tareas se publicaron este mes?"
                       enterKeyHint="send" className="flex-1 px-4 py-3 sm:py-2 border-2 border-border rounded-2xl sm:rounded-lg text-[16px] sm:text-sm focus:outline-none focus:border-[#8B5CF6]" />
                <button type="submit" disabled={loading || pregunta.length < 3}
                        className="px-4 py-3 sm:py-2 bg-[#7C3AED] text-white rounded-2xl sm:rounded-lg text-[16px] sm:text-sm font-extrabold disabled:opacity-50 inline-flex items-center justify-center gap-1.5">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  Preguntar
                </button>
              </form>
              <div className="text-[12px] text-muted-foreground mt-2 hidden sm:block">
                Atajo: <kbd className="px-1 bg-muted rounded">Ctrl</kbd>+<kbd className="px-1 bg-muted rounded">J</kbd>
              </div>
            </div>

            {/* Resultado o sugerencias */}
            <div className="max-h-[400px] max-sm:max-h-none max-sm:flex-1 overflow-y-auto p-4 max-sm:pb-[max(16px,env(safe-area-inset-bottom))]">
              {loading && (
                <div className="bg-[#F5F3FF] border border-[#DDD6FE] rounded-lg p-4 flex flex-col items-center gap-2 text-center">
                  <Loader2 className="h-5 w-5 text-[#7C3AED] animate-spin" />
                  <div className="text-sm text-foreground font-medium">Crew Brain está pensando…</div>
                  <div className="text-[12px] text-muted-foreground">Generando SQL y consultando la base — puede tardar unos segundos</div>
                </div>
              )}

              {resultado?.error && (
                <div className="bg-[#FEF2F2] border border-[#FECACA] rounded-lg p-3 text-sm text-[#B91C1C]">
                  {resultado.error}
                </div>
              )}

              {resultado && !resultado.error && (
                <>
                  <div className="bg-[#F5F3FF] border border-[#DDD6FE] rounded-lg p-3 mb-3">
                    <div className="flex items-start gap-2">
                      <Sparkles className="h-4 w-4 text-[#7C3AED] mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <div className="text-sm text-foreground whitespace-pre-wrap">{resultado.respuesta}</div>
                        <div className="text-[12px] text-muted-foreground mt-1.5">
                          {resultado.total} filas · {resultado.elapsed_ms}ms
                        </div>
                      </div>
                    </div>
                  </div>

                  {resultado.filas?.length > 0 && (
                    <div className="border border-border rounded-lg overflow-hidden">
                      <div className="bg-muted px-3 py-1.5 text-[12px] font-semibold uppercase text-muted-foreground border-b border-border">
                        Datos
                      </div>
                      <div className="overflow-x-auto max-h-64">
                        <table className="w-full text-xs">
                          <thead className="bg-muted border-b border-border sticky top-0">
                            <tr>
                              {resultado.columnas.map(c => (
                                <th key={c} className="text-left px-2 py-1.5 font-semibold text-muted-foreground">{c}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {resultado.filas.slice(0, 50).map((f, i) => (
                              <tr key={i} className="border-b border-border">
                                {resultado.columnas.map(c => (
                                  <td key={c} className="px-2 py-1 truncate max-w-[200px]"
                                      title={String(f[c] ?? "")}>
                                    {String(f[c] ?? "—")}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  <details className="mt-2">
                    <summary className="text-[12px] text-muted-foreground cursor-pointer hover:text-muted-foreground">Ver SQL</summary>
                    <pre className="text-[12px] bg-[#111318] text-[#6EE7B7] p-2 rounded mt-1 overflow-x-auto">{resultado.sql}</pre>
                  </details>
                </>
              )}

              {!resultado && !loading && (
                <div>
                  <div className="text-xs font-semibold text-muted-foreground uppercase mb-2">Sugerencias</div>
                  <div className="space-y-1.5">
                    {SUGERENCIAS.map(s => (
                      <button key={s} onClick={() => preguntar(s)}
                              className="w-full text-left px-4 py-3 min-h-[52px] text-[16px] sm:text-sm font-semibold sm:font-normal bg-muted hover:bg-[#F5F3FF] hover:border-[#DDD6FE] border border-border rounded-2xl sm:rounded-lg transition-colors">
                        {s}
                      </button>
                    ))}
                  </div>

                  {historial.length > 0 && (
                    <div className="mt-4">
                      <div className="text-xs font-semibold text-muted-foreground uppercase mb-2">Recientes</div>
                      <div className="space-y-1">
                        {historial.map((h, i) => (
                          <button key={i} onClick={() => preguntar(h.pregunta)}
                                  className="w-full text-left px-2 py-1.5 text-xs text-muted-foreground hover:bg-muted rounded">
                            <span className="font-medium">{h.pregunta}</span>
                            <span className="text-muted-foreground ml-2">({h.total} filas)</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
